import { parseCalendarDate } from "../../utils/date";
import { matchPhase, matchLocation } from "../Matches/matchDiscovery";
import { countValue, squadTime } from "../Matches/upcomingMatch";
import { hasRegistrationPolicy } from "../Matches/registrationPolicy";
import { matchPresentationPhase } from "../Matches/matchPresentation";

export const statusValue = entry => String(entry.registration?.status || "pending").toLowerCase();
export const activeEntry = entry => ["pending", "confirmed", "waitlisted", "checked_in"].includes(statusValue(entry));
export const statusLabel = value => ({ pending: "Pending approval", checked_in: "Checked in", dq: "DQ", dnf: "DNF" })[value]
  || String(value || "pending").replace(/_/g, " ").replace(/\b\w/g, character => character.toUpperCase());

export function groupMyMatches(rows, now = new Date()) {
  const groups = new Map();
  rows.forEach(entry => {
    const matchId = entry.registration?.matchId || entry.match?.matchId;
    const key = `${entry.family}:${matchId || entry.registration.registrationId}`;
    if (!groups.has(key)) {
      const match = entry.match || {};
      const date = parseCalendarDate(match.startDate || match.matchDate);
      groups.set(key, { key, family: entry.family, match, matchId, name: match.matchName || "Match details unavailable",
        date, phase: hasRegistrationPolicy(match) ? matchPresentationPhase(match, now) : matchPhase(match, now), entries: [],
        path: matchId ? `/matches/${entry.family}/${encodeURIComponent(matchId)}` : null,
        discipline: entry.family === "bullseye" ? "Bullseye" : "NRA ARC" });
    }
    groups.get(key).entries.push(entry);
  });
  const all = [...groups.values()];
  const upcoming = all.filter(group => !["completed", "cancelled"].includes(group.phase) && group.entries.some(activeEntry))
    .sort((a, b) => Number(b.phase === "in_progress") - Number(a.phase === "in_progress")
      || (a.date?.getTime() ?? Infinity) - (b.date?.getTime() ?? Infinity) || a.name.localeCompare(b.name));
  const past = all.filter(group => group.phase === "completed")
    .sort((a, b) => (b.date?.getTime() || 0) - (a.date?.getTime() || 0) || a.name.localeCompare(b.name));
  const inactive = all.filter(group => !upcoming.includes(group) && !past.includes(group));
  return { all, upcoming, past, inactive };
}

const number = value => (typeof value === "number" || (typeof value === "string" && value.trim() !== ""))
  && Number.isFinite(Number(value)) && Number(value) >= 0 ? Number(value) : null;

export function entryResult(entry) {
  const summary = entry.scoreSummary;
  const score = summary?.match;
  const status = String(score?.status || "").toLowerCase();
  if (["dq", "dnf", "withdrawn"].includes(status)) return { value: statusLabel(status), detail: "See match results" };
  const total = number(score?.totalScore);
  if (!score || total === null) return { value: "No score posted", detail: "Check match results for updates" };
  if (entry.family === "bullseye") {
    const xs = countValue(score.xCount);
    const shots = countValue(score.shotsFired);
    const planned = countValue(score.plannedShots);
    return { value: `${total.toFixed(0)}${xs !== null ? `–${xs}X` : " points"}`,
      detail: [shots !== null && planned !== null ? `${shots}/${planned} shots` : shots !== null ? `${shots} shots` : "",
        planned > 0 && shots !== null && shots < planned ? "Partial score" : "",
        ["official", "confirmed", "final"].includes(status) ? "Recorded scores confirmed" : status ? statusLabel(status) : "Score received"].filter(Boolean).join(" · ") };
  }
  const done = countValue(summary.stagesCompleted);
  const expected = countValue(summary.expectedStageCount);
  return { value: `${total.toFixed(2)} s`, detail: done !== null && expected > 0
    ? `${done}/${expected} stages confirmed${done < expected ? " · Partial score" : ""}` : "See match results for confirmation" };
}

export function entryFacts(entry, match) {
  const registration = { ...entry.registration.disciplineProfile, ...entry.registration };
  const squads = Array.isArray(match.squads) ? match.squads : [];
  const squad = squads.find(item => registration.squadId && (item.squadId || item.id) === registration.squadId)
    || squads.find(item => registration.squadNumber != null && String(item.squadNumber ?? item.number) === String(registration.squadNumber));
  const squadNumber = squad?.squadNumber ?? squad?.number ?? registration.squadNumber;
  const squadLabel = squad?.label || registration.squadLabel || (squadNumber != null ? `Squad ${squadNumber}` : "Squad to be assigned");
  const time = squad?.scheduledAt || squad?.squadScheduledAt || squad?.matchTime || registration.squadScheduledAt;
  return entry.family === "bullseye" ? [
    ["Relay", registration.relayLabel || "To be assigned"],
    ["Firing point", registration.firingPointLabel || "To be assigned"],
    ["Classification", registration.classification || "Not listed"],
  ] : [["Squad", squadLabel], ["Start time", time ? squadTime(time, match.competitorInfo?.publicationState === "published" ? match.competitorInfo.timeZone || match.timeZone : match.timeZone) : "To be announced"],
    ["Division", registration.division || registration.divisionId || "Not listed"]];
}

export function matchesHistoryFilter(group, query, family, status) {
  return (family === "all" || family === group.family)
    && (status === "all" || group.entries.some(entry => statusValue(entry) === status))
    && (!query.trim() || [group.name, group.discipline, matchLocation(group.match), ...group.entries.map(entry => entry.registration.displayName)]
      .some(value => String(value || "").toLowerCase().includes(query.trim().toLowerCase())));
}
